import React from "react";
import { useSocketLog, useConnectedScreens } from "../hooks/useSocket";

interface SocketLogModalProps {
  open: boolean;
  onClose: () => void;
  isConnected: boolean;
}

const formatTime = (ts: number) => {
  const d = new Date(ts);
  return d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

/**
 * Overlay panel that lists recent socket messages (sent + received).
 * Self-contained (inline styles) so it renders consistently on both the
 * Tailwind dashboard and the antd live display.
 */
const SocketLogModal: React.FC<SocketLogModalProps> = ({
  open,
  onClose,
  isConnected,
}) => {
  const { messages, clearMessages } = useSocketLog();
  const screens = useConnectedScreens();

  if (!open) return null;

  const ordered = [...messages].reverse(); // newest first

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(680px, 100%)",
          maxHeight: "80vh",
          backgroundColor: "#111827",
          color: "#e5e7eb",
          borderRadius: 12,
          boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 16px",
            borderBottom: "1px solid #1f2937",
          }}
        >
          <span
            title={isConnected ? "Socket connected" : "Socket disconnected"}
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: isConnected ? "#22c55e" : "#ef4444",
              boxShadow: `0 0 6px ${isConnected ? "#22c55e" : "#ef4444"}`,
              flexShrink: 0,
            }}
          />
          <span style={{ fontWeight: 600, fontSize: 14 }}>
            Socket messages
          </span>
          <span style={{ fontSize: 12, color: isConnected ? "#86efac" : "#fca5a5" }}>
            {isConnected ? "socket connected" : "socket disconnected"}
          </span>
          <span style={{ fontSize: 12, color: "#9ca3af" }}>
            ({messages.length})
          </span>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={clearMessages}
              style={{
                background: "transparent",
                color: "#9ca3af",
                border: "1px solid #374151",
                borderRadius: 6,
                padding: "4px 10px",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "transparent",
                color: "#9ca3af",
                border: "1px solid #374151",
                borderRadius: 6,
                padding: "4px 10px",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>

        {/* Connected screens (TVs) */}
        <div
          style={{
            padding: "10px 16px",
            borderBottom: "1px solid #1f2937",
            backgroundColor: "#0b1220",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: screens.length > 0 ? 8 : 0,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: screens.length > 0 ? "#22c55e" : "#6b7280",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                color: screens.length > 0 ? "#86efac" : "#9ca3af",
              }}
            >
              {screens.length > 0
                ? `${screens.length} TV${screens.length > 1 ? "s" : ""} connected`
                : "No TVs connected"}
            </span>
          </div>

          {screens.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {screens.map((screen: any) => (
                <div
                  key={screen.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    padding: "6px 10px",
                    borderRadius: 8,
                    backgroundColor: "#111827",
                    border: "1px solid #1f2937",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#e5e7eb",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {screen.name || screen.id}
                    </div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>
                      {[screen.platform, screen.resolution, screen.version]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 11,
                      color: "#6b7280",
                      flexShrink: 0,
                      alignSelf: "center",
                    }}
                  >
                    since {formatTime(screen.connectedAt)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: 12 }}>
          {ordered.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                color: "#6b7280",
                padding: "32px 0",
                fontSize: 13,
              }}
            >
              No messages yet.
            </div>
          ) : (
            ordered.map((msg) => {
              const sent = msg.direction === "sent";
              const innerTopic =
                msg.data && typeof msg.data === "object"
                  ? (msg.data as any).topic
                  : undefined;
              return (
                <div
                  key={msg.id}
                  style={{
                    display: "flex",
                    gap: 10,
                    padding: "8px 10px",
                    borderRadius: 8,
                    marginBottom: 6,
                    backgroundColor: "#0b1220",
                    border: "1px solid #1f2937",
                  }}
                >
                  <span
                    style={{
                      flexShrink: 0,
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "2px 6px",
                      borderRadius: 4,
                      height: "fit-content",
                      color: sent ? "#93c5fd" : "#86efac",
                      backgroundColor: sent
                        ? "rgba(59,130,246,0.15)"
                        : "rgba(34,197,94,0.15)",
                    }}
                  >
                    {sent ? "SENT" : "RECV"}
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 8,
                        marginBottom: 2,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#e5e7eb",
                          wordBreak: "break-all",
                        }}
                      >
                        {msg.topic}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color: "#6b7280",
                          flexShrink: 0,
                        }}
                      >
                        {formatTime(msg.timestamp)}
                      </span>
                    </div>
                    {innerTopic !== undefined && innerTopic !== null && (
                      <span
                        style={{
                          fontSize: 11,
                          color: "#9ca3af",
                          wordBreak: "break-all",
                        }}
                      >
                        {String(innerTopic)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default SocketLogModal;
